import { SxProps, Theme } from '@mui/material';
import { brandAlpha, brandColors, coarsePointerOnly, hoverOnly } from '../../../theme/mixins';

export const weekBarStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    mb: 1.5,
};

export const weekRangeStyles: SxProps<Theme> = {
    flex: 1,
    fontWeight: 600,
    textAlign: 'center',
};

/**
 * Seven equal columns at every width.
 *
 * `1fr` rather than a `minmax()` floor on purpose: a week is always seven days, and a hard
 * pixel floor would push a 320px phone sideways. The day cells shrink instead, which they
 * can afford to -- each holds a letter, two digits and a count.
 */
export const weekStripStyles: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 0.75,
};

/*
 * The base and the selected variant are each written out in full rather than merged at the
 * call site: a `*.styles.ts` is where a style lives in this app, and spreading two of them
 * inside the TSX would put an object literal back in the component.
 */
const dayCellBase = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 0.25,
    px: 0.25,
    py: 1,
    minWidth: 0,
    borderRadius: 2.5,
    border: '1px solid',
    borderColor: 'grey.200',
    backgroundColor: 'background.paper',
    color: 'text.primary',
    textTransform: 'none',
    ...coarsePointerOnly({ minHeight: 64 }),
    ...hoverOnly({
        borderColor: brandColors.primaryLight,
        backgroundColor: brandAlpha('primary', 0.04),
    }),
    '&.Mui-disabled': {
        backgroundColor: 'grey.50',
        color: 'text.disabled',
        borderColor: 'grey.100',
    },
};

export const dayCellStyles: SxProps<Theme> = dayCellBase;

/** The day being looked at. Filled, so it reads as chosen rather than merely hovered. */
export const dayCellSelectedStyles: SxProps<Theme> = {
    ...dayCellBase,
    backgroundColor: brandColors.primary,
    borderColor: brandColors.primary,
    color: brandColors.primaryContrast,
    ...hoverOnly({
        backgroundColor: brandColors.primaryDark,
        borderColor: brandColors.primaryDark,
    }),
};

export const dayWeekdayStyles: SxProps<Theme> = {
    fontSize: '0.65rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    lineHeight: 1.2,
    color: 'inherit',
    opacity: 0.75,
};

export const dayNumberStyles: SxProps<Theme> = {
    fontSize: '1.05rem',
    fontWeight: 600,
    lineHeight: 1.2,
    color: 'inherit',
};

export const dayCountStyles: SxProps<Theme> = {
    fontSize: '0.62rem',
    lineHeight: 1.2,
    color: 'inherit',
    opacity: 0.75,
    // Never wraps a count onto a second line and pushes the cell taller than its siblings.
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
};

/** Today, marked whether or not it is the day being looked at. */
export const todayMarkStyles: SxProps<Theme> = {
    width: 16,
    height: 2.5,
    borderRadius: 2,
    backgroundColor: brandColors.secondary,
};

export const periodHeadingStyles: SxProps<Theme> = {
    mt: 2,
    mb: 1,
    fontSize: '0.74rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'text.secondary',
};

/**
 * The chips.
 *
 * `min(78px, 100%)` rather than a bare `78px` floor: below that width a hard floor stops
 * being a minimum and starts being a reason the page scrolls sideways.
 */
export const slotGridStyles: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(78px, 100%), 1fr))',
    gap: 1,
};

const slotChipBase = {
    borderRadius: 2.5,
    borderColor: 'grey.200',
    color: brandColors.primary,
    fontWeight: 600,
    textTransform: 'none',
    ...coarsePointerOnly({ minHeight: 44 }),
    ...hoverOnly({
        borderColor: brandColors.primaryLight,
        backgroundColor: brandAlpha('primary', 0.06),
    }),
};

export const slotChipStyles: SxProps<Theme> = slotChipBase;

export const slotChipSelectedStyles: SxProps<Theme> = {
    ...slotChipBase,
    backgroundColor: brandColors.primary,
    borderColor: brandColors.primary,
    color: brandColors.primaryContrast,
    ...hoverOnly({
        backgroundColor: brandColors.primaryDark,
        borderColor: brandColors.primaryDark,
    }),
};

/** The notice under the strip -- a caption, since it qualifies the strip above it. */
export const noticeHintStyles: SxProps<Theme> = {
    mt: 1.5,
    fontSize: '0.82rem',
    color: 'text.secondary',
};

export const emptyWeekStyles: SxProps<Theme> = {
    py: 3,
    color: 'text.secondary',
};

export const loadingBoxStyles: SxProps<Theme> = {
    display: 'flex',
    justifyContent: 'center',
    py: 4,
};
